/**
 * ProjectSettings — Zentrale Konfigurationsseite pro Projekt.
 * Zugriff: Admin + Projektleiter (Nachunternehmer/Mitarbeiter → Redirect zum Dashboard).
 *
 * Beinhaltet alle technischen Schalter, die zuvor als Kacheln auf dem Dashboard lagen:
 *   - Abrechnung (Stundensatz, Budget, geplante Stunden, km, Fahrzeit, Spesen)
 *   - Baustellen-Regeln (Foto-Pflicht)
 *   - GPS / Geofencing
 *   - Dokumentation (Sichtbarkeit)
 *   - Gefahren / Hinweise
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, authFetch } from "../App";
import { toast } from "sonner";
import {
  Settings, Car, Timer, Receipt, Image as ImageIcon, Shield, Clock,
  Building2, Radar, AlertTriangle, FileText, FolderOpen, Hotel, ArrowLeft,
  Euro, Calculator, ChevronLeft, Save, Loader2, Zap, Link as LinkIcon,
  RefreshCw, ExternalLink, X, CheckCircle2, XCircle,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import ProjectTopNav from "../components/ProjectTopNav";

const API = "/api";
const AUTO_SAVE_DELAY = 800;

export default function ProjectSettings() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const pendingRef = useRef(null);
  const saveTimerRef = useRef(null);

  const role = (user?.role || "").toLowerCase();
  const isPrivileged = role === "admin" || role === "projektleiter";

  // Permission Guard: andere Rollen direkt rauswerfen
  useEffect(() => {
    if (user && !isPrivileged) {
      toast.error("Keine Berechtigung — Einstellungen sind nur für Admin/Projektleiter sichtbar");
      navigate(`/app/projekte/${projectId}/dashboard`, { replace: true });
    }
  }, [user, isPrivileged, navigate, projectId]);

  // Load project
  useEffect(() => {
    if (!projectId) return;
    (async () => {
      setLoading(true);
      try {
        const r = await authFetch(`${API}/projects`);
        if (r.ok) {
          const list = await r.json();
          const p = list.find((x) => x.id === projectId);
          if (p) setProject(p);
          else toast.error("Projekt nicht gefunden");
        }
      } catch (e) {
        toast.error("Fehler beim Laden");
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  const patchProject = useCallback(async (fields) => {
    setSaveStatus("saving");
    try {
      const res = await authFetch(`${API}/projects/${projectId}`, {
        method: "PATCH",
        body: JSON.stringify(fields),
      });
      if (res.ok) {
        const updated = await res.json();
        setProject((prev) => {
          if (!prev) return updated;
          const pending = pendingRef.current || {};
          const merged = { ...prev, ...updated };
          Object.keys(pending).forEach((k) => { merged[k] = pending[k]; });
          return merged;
        });
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 1500);
      } else {
        setSaveStatus("error");
        toast.error("Fehler beim Speichern");
        setTimeout(() => setSaveStatus("idle"), 2500);
      }
    } catch {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 2500);
    }
  }, [projectId]);

  const saveField = useCallback((field, value) => {
    setProject((prev) => ({ ...prev, [field]: value }));
    patchProject({ [field]: value });
  }, [patchProject]);

  const saveFieldDebounced = useCallback((field, value) => {
    setProject((prev) => ({ ...prev, [field]: value }));
    pendingRef.current = { ...(pendingRef.current || {}), [field]: value };
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      if (pendingRef.current) {
        patchProject(pendingRef.current);
        pendingRef.current = null;
      }
    }, AUTO_SAVE_DELAY);
  }, [patchProject]);

  const saveGeofencing = useCallback((key, value) => {
    const geo = {
      ...(project?.geofencing || { enabled: false, radius: 200, auto_clock_out: false, tolerance_minutes: 5, indoor_mode: false }),
      [key]: value,
    };
    setProject((prev) => ({ ...prev, geofencing: geo }));
    pendingRef.current = { ...(pendingRef.current || {}), geofencing: geo };
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      if (pendingRef.current) {
        patchProject(pendingRef.current);
        pendingRef.current = null;
      }
    }, AUTO_SAVE_DELAY);
  }, [project, patchProject]);

  if (loading) {
    return (
      <div className="p-8 text-center text-sm text-gray-500" data-testid="settings-loading">
        <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Lade Projekt-Einstellungen…
      </div>
    );
  }
  if (!project) {
    return <div className="p-8 text-center text-sm text-gray-500">Projekt nicht gefunden.</div>;
  }
  if (!isPrivileged) return null;

  const geo = project.geofencing || { enabled: false, radius: 200, auto_clock_out: false, tolerance_minutes: 5, indoor_mode: false };
  const hasAddress = project.address?.lat && project.address?.lng;

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto" data-testid="project-settings-page">
      <ProjectTopNav projectId={projectId} />
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to={`/app/projekte/${projectId}/dashboard`}
            className="p-1.5 rounded-md hover:bg-white/10 text-white/70 hover:text-white"
            title="Zurück zum Dashboard"
            data-testid="settings-back"
          >
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <Settings className="w-6 h-6 text-blue-400 flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-white truncate">Projekt-Einstellungen</h1>
            <p className="text-xs text-white/50 truncate">{project.name}</p>
          </div>
        </div>
        <SaveBadge status={saveStatus} />
      </div>

      {/* Section: Abrechnung */}
      <SettingsSection icon={Euro} title="Abrechnung" testId="section-abrechnung">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Abrechnungsart">
            <Select value={project.billing_type || "stunden_abrechenbar"} onValueChange={(v) => saveField("billing_type", v)}>
              <SelectTrigger className="h-9 text-sm" data-testid="settings-billing-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="stunden_abrechenbar">Stunden abrechenbar</SelectItem>
                <SelectItem value="pauschal">Pauschal</SelectItem>
                <SelectItem value="festpreis">Festpreis</SelectItem>
                <SelectItem value="intern">Intern (nicht abrechenbar)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Standard-Stundensatz (€)">
            <Input
              type="number" step="0.01" min="0"
              value={project.default_hourly_rate ?? ""}
              onChange={(e) => saveFieldDebounced("default_hourly_rate", e.target.value === "" ? null : parseFloat(e.target.value))}
              className="h-9 text-sm"
              data-testid="settings-hourly-rate"
            />
          </Field>
          <Field label="Budget (€)">
            <Input
              type="number" step="0.01" min="0"
              value={project.budget ?? ""}
              onChange={(e) => saveFieldDebounced("budget", e.target.value === "" ? null : parseFloat(e.target.value))}
              className="h-9 text-sm"
              data-testid="settings-budget"
            />
          </Field>
          <Field label="Geplante Stunden">
            <Input
              type="number" step="0.5" min="0"
              value={project.planned_hours ?? ""}
              onChange={(e) => saveFieldDebounced("planned_hours", e.target.value === "" ? null : parseFloat(e.target.value))}
              className="h-9 text-sm"
              data-testid="settings-planned-hours"
            />
          </Field>
        </div>

        <ToggleRow
          icon={Car} label="Kilometerabrechnung aktiv"
          checked={project.km_billing_active ?? true}
          onChange={(v) => saveField("km_billing_active", v)}
          testId="settings-km-billing"
        />
        {project.km_billing_active && (
          <Field label="Kosten pro km (€)" inline>
            <Input
              type="number" step="0.01" min="0"
              value={project.km_cost_per_km ?? 0.30}
              onChange={(e) => saveFieldDebounced("km_cost_per_km", e.target.value === "" ? 0.30 : parseFloat(e.target.value))}
              className="h-8 text-sm w-32"
              data-testid="settings-km-cost"
            />
          </Field>
        )}
        <ToggleRow
          icon={Timer} label="Fahrzeit abrechenbar"
          checked={project.travel_time_billable ?? true}
          onChange={(v) => saveField("travel_time_billable", v)}
          testId="settings-travel-time"
        />
        <ToggleRow
          icon={Receipt} label="Spesen relevant"
          checked={project.expenses_relevant ?? false}
          onChange={(v) => saveField("expenses_relevant", v)}
          testId="settings-expenses"
        />
      </SettingsSection>

      {/* Section: Baustellen-Regeln */}
      <SettingsSection icon={Shield} title="Baustellen-Regeln" testId="section-baustellen">
        <ToggleRow
          icon={ImageIcon} label="Foto-Pflicht bei Erledigt"
          description="Aufgaben können erst als erledigt markiert werden, wenn ein Foto angehängt ist."
          checked={project.require_photo_for_completion ?? false}
          onChange={(v) => saveField("require_photo_for_completion", v)}
          testId="settings-photo-required"
        />
      </SettingsSection>

      {/* Section: GPS / Geofencing */}
      <SettingsSection icon={Radar} title="GPS / Geofencing" testId="section-geofencing">
        {!hasAddress && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700">
              Geofencing benötigt eine Projektadresse mit Koordinaten. Bitte Adresse im Dashboard eintragen.
            </p>
          </div>
        )}
        <ToggleRow
          icon={Shield} label="Geofencing aktiv"
          description="GPS-Radius-Prüfung beim Stempeln"
          checked={geo.enabled}
          onChange={(v) => saveGeofencing("enabled", v)}
          disabled={!hasAddress}
          testId="settings-geo-enabled"
        />
        {geo.enabled && (
          <div className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Radius</Label>
                <span className="text-xs font-mono font-semibold text-blue-400">{geo.radius || 200}m</span>
              </div>
              <input
                type="range" min="50" max="500" step="25"
                value={geo.radius || 200}
                onChange={(e) => saveGeofencing("radius", parseInt(e.target.value))}
                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-blue-500"
                data-testid="settings-geo-radius"
              />
              <div className="flex justify-between text-[10px] text-white/40">
                <span>50m</span><span>200m</span><span>500m</span>
              </div>
            </div>
            <Field label="Toleranzzeit (Minuten)" inline>
              <Select
                value={String(geo.tolerance_minutes || 5)}
                onValueChange={(v) => saveGeofencing("tolerance_minutes", parseInt(v))}
              >
                <SelectTrigger className="h-8 text-sm w-24" data-testid="settings-geo-tolerance">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2, 3, 5, 10].map((m) => (
                    <SelectItem key={m} value={String(m)}>{m} Min.</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <ToggleRow
              icon={Clock} label="Auto-Ausstempeln"
              description="Automatisch ausstempeln bei Verlassen des Radius"
              checked={geo.auto_clock_out}
              onChange={(v) => saveGeofencing("auto_clock_out", v)}
              testId="settings-geo-auto-clockout"
            />
            <ToggleRow
              icon={Building2} label="Indoor / Baustellen-Modus"
              description="Geofencing deaktivieren (z.B. für Hallen ohne GPS-Empfang)"
              checked={geo.indoor_mode}
              onChange={(v) => saveGeofencing("indoor_mode", v)}
              testId="settings-geo-indoor"
            />
          </div>
        )}
      </SettingsSection>

      {/* Section: Dokumentation */}
      <SettingsSection icon={FolderOpen} title="Dokumentation" testId="section-dokumentation">
        <InfoRow icon={FileText} label="Bautagesberichte">
          <Link
            to={`/app/projekte/${projectId}/berichte`}
            className="text-xs text-blue-400 hover:underline"
          >
            Berichte öffnen →
          </Link>
        </InfoRow>
        <InfoRow icon={FolderOpen} label="Projekt-Dokumente">
          <Link
            to={`/app/projekte/${projectId}/dokumente`}
            className="text-xs text-blue-400 hover:underline"
          >
            Dokumente öffnen →
          </Link>
        </InfoRow>
        <InfoRow icon={Hotel} label="Unterkünfte">
          <Link
            to={`/app/projekte/${projectId}/dashboard`}
            className="text-xs text-blue-400 hover:underline"
          >
            Im Dashboard verwalten →
          </Link>
        </InfoRow>
      </SettingsSection>

      {/* Section: Hinweise für Nachunternehmer (sichtbar in mobiler Projekt-Card) */}
      <SettingsSection icon={FileText} title={project.subcontractor_notes_title || "Hinweise für Nachunternehmer"} testId="section-nu-hinweise">
        <p className="text-xs text-white/55 mb-2 -mt-1">
          Diese Hinweise sehen Nachunternehmer beim Tippen auf das Info-Symbol „i&rdquo; in der mobilen Projektliste.
          Ideal für Vorgaben zur Rechnungserstellung, Leistungsbeschreibung, Stundennachweisen oder Ansprechpartner.
        </p>
        <Field label="Überschrift (sichtbar in App)" full>
          <input
            type="text"
            value={project.subcontractor_notes_title || ""}
            onChange={(e) => saveFieldDebounced("subcontractor_notes_title", e.target.value)}
            placeholder="Hinweise für Nachunternehmer"
            className="w-full px-3 py-2 text-sm rounded-md border border-white/10 bg-white/5 text-white placeholder:text-white/30"
            data-testid="settings-subcontractor-notes-title"
          />
        </Field>
        <Field label="Hinweise (mehrzeilig)" full>
          <textarea
            rows={6}
            value={project.subcontractor_notes || ""}
            onChange={(e) => saveFieldDebounced("subcontractor_notes", e.target.value)}
            placeholder="Beispiel: Bitte Rechnung mit Projektname, Leistungszeitraum, Stundenanzahl und kurzer Leistungsbeschreibung einreichen. Stundennachweis als PDF oder Foto beifügen. Rechnung an rechnung@tgaone.de. Ansprechpartner: Ronny Pforr."
            className="w-full px-3 py-2 text-sm rounded-md border border-white/10 bg-white/5 text-white placeholder:text-white/30"
            data-testid="settings-subcontractor-notes"
          />
        </Field>
        <Field label="Für Nachunternehmer sichtbar" inline>
          <input
            type="checkbox"
            checked={project.subcontractor_notes_visible !== false}
            onChange={(e) => saveFieldDebounced("subcontractor_notes_visible", e.target.checked)}
            className="w-4 h-4 accent-blue-500 cursor-pointer"
            data-testid="settings-subcontractor-notes-visible"
          />
        </Field>
        {project.subcontractor_notes && project.subcontractor_notes_visible === false && (
          <div className="text-[11px] text-amber-300/80 bg-amber-500/10 border border-amber-400/20 rounded-md px-3 py-2">
            Hinweise sind hinterlegt, aber für Nachunternehmer aktuell ausgeblendet.
          </div>
        )}
      </SettingsSection>

      {/* Section: Gefahren / Hinweise */}
      <SettingsSection icon={AlertTriangle} title="Gefahren / Hinweise" testId="section-hinweise">
        <Field label="Sicherheitshinweise" full>
          <textarea
            rows={3}
            value={project.safety_notes || ""}
            onChange={(e) => saveFieldDebounced("safety_notes", e.target.value)}
            placeholder="z.B. Helmpflicht, Asbestverdacht, enge Schächte …"
            className="w-full px-3 py-2 text-sm rounded-md border border-white/10 bg-white/5 text-white placeholder:text-white/30"
            data-testid="settings-safety-notes"
          />
        </Field>
        <Field label="Interne Notizen" full>
          <textarea
            rows={3}
            value={project.special_notes || ""}
            onChange={(e) => saveFieldDebounced("special_notes", e.target.value)}
            placeholder="Interne Hinweise — nicht für Kunden sichtbar"
            className="w-full px-3 py-2 text-sm rounded-md border border-white/10 bg-white/5 text-white placeholder:text-white/30"
            data-testid="settings-special-notes"
          />
        </Field>
      </SettingsSection>
      {/* Section: Google Drive */}
      <GoogleDriveSection projectId={projectId} project={project} onUpdated={setProject} />

    </div>
  );
}

function GoogleDriveSection({ projectId, project, onUpdated }) {
  const [status, setStatus] = useState({
    connected: !!project?.gdrive_folder_id,
    folder_id: project?.gdrive_folder_id || null,
    folder_name: project?.gdrive_folder_name || null,
    folder_url: project?.gdrive_folder_url || null,
    connected_at: project?.gdrive_connected_at || null,
  });
  const [editing, setEditing] = useState(false);
  const [folderInput, setFolderInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/nina-engine/tools/drive/projects/${projectId}/status`);
      if (res.ok) {
        const j = await res.json();
        setStatus(j.data);
      }
    } catch { /* ignore */ }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const extractFolderId = (raw) => {
    const s = (raw || "").trim();
    const m = s.match(/\/folders\/([a-zA-Z0-9_-]+)/);
    if (m) return m[1];
    return s;
  };

  const connect = async () => {
    const fid = extractFolderId(folderInput);
    if (!fid || fid.length < 10) {
      toast.error("Bitte eine gültige Drive-Folder-ID oder -URL eingeben.");
      return;
    }
    setBusy(true);
    try {
      const res = await authFetch(`${API}/nina-engine/tools/drive/projects/${projectId}/connect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ folder_id: fid, folder_url: folderInput.includes("http") ? folderInput : null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail?.message || "Verbindung fehlgeschlagen");
      }
      const j = await res.json();
      toast.success(`Verbunden mit ${j.data.folder_name}`);
      setEditing(false);
      setFolderInput("");
      onUpdated?.((prev) => ({ ...prev, gdrive_folder_id: j.data.folder_id, gdrive_folder_name: j.data.folder_name, gdrive_folder_url: j.data.folder_url, gdrive_connected: true, gdrive_connected_at: j.data.connected_at }));
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Verbindung zu Google Drive für dieses Projekt wirklich entfernen? Dateien in Drive bleiben erhalten.")) return;
    setBusy(true);
    try {
      const res = await authFetch(`${API}/nina-engine/tools/drive/projects/${projectId}/connection`, { method: "DELETE" });
      if (!res.ok) throw new Error("Trennen fehlgeschlagen");
      toast.success("Verbindung entfernt");
      onUpdated?.((prev) => ({ ...prev, gdrive_folder_id: null, gdrive_folder_name: null, gdrive_folder_url: null, gdrive_connected: false }));
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally { setBusy(false); }
  };

  const runTest = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const res = await authFetch(`${API}/nina-engine/tools/drive/projects/${projectId}/files?limit=1`);
      const j = await res.json();
      const ok = !!j?.success && Array.isArray(j?.data?.files);
      setTestResult({ ok, count: j?.data?.count ?? 0 });
      toast.success(ok ? `Test OK — ${j.data.count} Dateien im Ordner` : "Test fehlgeschlagen");
    } catch (e) {
      setTestResult({ ok: false, error: e.message });
      toast.error(e.message);
    } finally { setBusy(false); }
  };

  return (
    <SettingsSection icon={Zap} title="Google Drive" testId="section-gdrive">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          {status.connected ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span className="text-white/90" data-testid="gdrive-status">Verbunden</span>
              <span className="text-white/50">·</span>
              <span className="text-white/70 font-mono text-xs">{status.folder_name || status.folder_id}</span>
            </>
          ) : (
            <>
              <XCircle className="w-4 h-4 text-white/40" />
              <span className="text-white/70" data-testid="gdrive-status">Nicht verbunden</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {status.connected && status.folder_url && (
            <a
              href={status.folder_url}
              target="_blank"
              rel="noreferrer"
              className="text-xs px-2.5 py-1.5 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 flex items-center gap-1.5 text-white/80"
              data-testid="gdrive-open-btn"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Ordner öffnen
            </a>
          )}
          {status.connected && (
            <button
              onClick={runTest}
              disabled={busy}
              className="text-xs px-2.5 py-1.5 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 flex items-center gap-1.5 text-white/80"
              data-testid="gdrive-test-btn"
            >
              <RefreshCw className={"w-3.5 h-3.5 " + (busy ? "animate-spin" : "")} /> Verbindung testen
            </button>
          )}
          <button
            onClick={() => setEditing((v) => !v)}
            disabled={busy}
            className="text-xs px-2.5 py-1.5 rounded-md border border-blue-400/40 bg-blue-500/10 hover:bg-blue-500/20 flex items-center gap-1.5 text-blue-200"
            data-testid="gdrive-edit-btn"
          >
            <LinkIcon className="w-3.5 h-3.5" /> {status.connected ? "Verbindung ändern" : "Ordner verbinden"}
          </button>
          {status.connected && (
            <button
              onClick={disconnect}
              disabled={busy}
              className="text-xs px-2.5 py-1.5 rounded-md border border-red-400/40 bg-red-500/10 hover:bg-red-500/20 flex items-center gap-1.5 text-red-300"
              data-testid="gdrive-disconnect-btn"
            >
              <X className="w-3.5 h-3.5" /> Entfernen
            </button>
          )}
        </div>
      </div>

      {editing && (
        <div className="mt-2 p-3 rounded-md border border-white/10 bg-white/5 space-y-2">
          <div className="text-[11px] text-white/60">
            Google-Drive-Ordner-ID oder URL einfügen (z.B. <code className="text-white/80">https://drive.google.com/drive/folders/…</code>).
            Die Zuordnung erfolgt über die Folder-ID, nicht über den Namen.
          </div>
          <input
            type="text"
            value={folderInput}
            onChange={(e) => setFolderInput(e.target.value)}
            placeholder="https://drive.google.com/drive/folders/… oder Folder-ID"
            className="w-full px-3 py-2 text-sm rounded-md border border-white/10 bg-white/5 text-white placeholder:text-white/30 font-mono"
            data-testid="gdrive-folder-input"
          />
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => { setEditing(false); setFolderInput(""); }}
              className="text-xs px-3 py-1.5 rounded-md border border-white/10 text-white/70"
              data-testid="gdrive-cancel-btn"
            >
              Abbrechen
            </button>
            <button
              onClick={connect}
              disabled={busy || !folderInput.trim()}
              className="text-xs px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50"
              data-testid="gdrive-connect-btn"
            >
              {busy ? "Verbinde …" : "Verbinden"}
            </button>
          </div>
        </div>
      )}

      {status.connected && status.folder_id && (
        <div className="mt-1 text-[11px] text-white/50 flex items-center gap-3 flex-wrap">
          <span>Folder-ID: <code className="text-white/70">{status.folder_id}</code></span>
          {status.connected_at && <span>· Verbunden am: {new Date(status.connected_at).toLocaleString("de-DE")}</span>}
        </div>
      )}

      {testResult && (
        <div
          className={"mt-1 text-xs px-3 py-2 rounded-md border " + (testResult.ok
            ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-200"
            : "border-red-400/40 bg-red-500/10 text-red-200")}
          data-testid="gdrive-test-result"
        >
          {testResult.ok
            ? `Test OK — ${testResult.count} Dateien im Ordner sichtbar.`
            : `Fehler: ${testResult.error || "unbekannt"}`}
        </div>
      )}
    </SettingsSection>
  );
}


function SettingsSection({ icon: Icon, title, testId, children }) {
  return (
    <section
      className="mb-5 rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-4"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
        <Icon className="w-4 h-4 text-blue-400 flex-shrink-0" />
        <h2 className="text-sm font-semibold text-white">{title}</h2>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children, inline = false, full = false }) {
  if (inline) {
    return (
      <div className="flex items-center justify-between gap-3 p-2.5 rounded-md border border-white/10 bg-white/[0.02]">
        <Label className="text-xs text-white/70">{label}</Label>
        {children}
      </div>
    );
  }
  return (
    <label className={`flex flex-col gap-1 ${full ? "" : ""}`}>
      <span className="text-xs text-white/60">{label}</span>
      {children}
    </label>
  );
}

function ToggleRow({ icon: Icon, label, description, checked, onChange, disabled, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 p-2.5 rounded-md border border-white/10 bg-white/[0.02]">
      <div className="flex items-start gap-2 min-w-0">
        <Icon className="w-4 h-4 text-white/50 mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-white">{label}</div>
          {description && <p className="text-[11px] text-white/50 mt-0.5">{description}</p>}
        </div>
      </div>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="w-4 h-4 rounded border-white/20 accent-blue-500 flex-shrink-0 disabled:opacity-40"
        data-testid={testId}
      />
    </div>
  );
}

function InfoRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-center justify-between gap-3 p-2.5 rounded-md border border-white/10 bg-white/[0.02]">
      <div className="flex items-center gap-2 min-w-0">
        <Icon className="w-4 h-4 text-white/50 flex-shrink-0" />
        <span className="text-sm text-white">{label}</span>
      </div>
      {children}
    </div>
  );
}

function SaveBadge({ status }) {
  if (status === "saving") {
    return (
      <span className="text-xs text-blue-400 flex items-center gap-1.5" data-testid="settings-save-status">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Speichern…
      </span>
    );
  }
  if (status === "saved") {
    return (
      <span className="text-xs text-emerald-400 flex items-center gap-1.5" data-testid="settings-save-status">
        <Save className="w-3.5 h-3.5" /> Gespeichert
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="text-xs text-red-400 flex items-center gap-1.5" data-testid="settings-save-status">
        <AlertTriangle className="w-3.5 h-3.5" /> Fehler
      </span>
    );
  }
  return null;
}
